/**
 * Util functions to be used within rows. The ./utils imports from rows, thus utils defined in the file can't be
 * used in rows (causes a circular dependency).
 */

import {DEFAULT_FILTER_OPERATOR, PropertyFilter} from 'common/widgets/property-filter-menu/models/property-filter';
import {Segmentation} from 'common/types/reports/query';
import {FullPropertyType, PropertyType, ResourceType} from 'common/report/constants';
import {BooleanOperator, DisplayContext, ListQuantifier} from 'common/widgets/property-filter-menu/models/types';
import {isLookupPropertyFilter, PropertyFilterFactory} from 'common/widgets/property-filter-menu/models/utils';
import {TimeUnit} from 'common/widgets/date-range-picker/models/types';

/**
 * list property object requires additional attributes for the UI.
 *
 * @param listProperty - The property to populate attributes of
 * @param forceList - Force treating the property as a list
 */
export function populateListPropertyAttrs<T extends Partial<Segmentation>>(listProperty: T, forceList?: boolean): T {
  // list quantifier UI for list properties
  const isList =
    forceList || listProperty.propertyType === PropertyType.List || listProperty.joinPropertyType === PropertyType.List;
  if (isList && listProperty.resourceType !== ResourceType.Cohort) {
    if (!isLookupPropertyFilter(listProperty)) {
      listProperty.joinPropertyType = PropertyType.List;
    }

    // if the subtype isn't already set, default it to string.
    const setDefaultSubType = listProperty.propertyType === PropertyType.List;
    if (setDefaultSubType) {
      listProperty.propertyType = PropertyType.String;
      listProperty.propertyDefaultType = PropertyType.String;
    }

    if (listProperty instanceof PropertyFilter) {
      listProperty.listQuantifier = ListQuantifier.Any;
      listProperty.filterOperator = DEFAULT_FILTER_OPERATOR[listProperty.propertyType];

      const isComplexListFilter = listProperty.propertyType === PropertyType.Object;
      if (isComplexListFilter) {
        listProperty.listItemFilters = [];
        listProperty.filterOperator = BooleanOperator.True;
        listProperty.filterValue = true;
      }
    }
  }

  return listProperty;
}

/**
 * Gets the full datatype of a segmentation.
 */
export function getPropertyType(segmentation: Partial<Segmentation>): FullPropertyType {
  return {
    type: segmentation.propertyType || segmentation.joinPropertyType,
    isList: segmentation.joinPropertyType === PropertyType.List,
  };
}

/**
 * Immutably typecasts a segmentation.
 */
export function setPropertyType<T extends Partial<Segmentation>>(
  segmentation: T,
  newType: FullPropertyType,
  forFilter?: boolean,
): T {
  let newSegmentation: Partial<Segmentation> = {
    ...segmentation,
    propertyType: newType.type,
    // These will be set by the populateListPropertyAttrs call if needed. Defaulting to null for non-lists
    listQuantifier: null,
    joinPropertyType: null,
    ...(forFilter
      ? {
          listItemFilters: null,
          filterOperator: null,
          filterValue: null,
        }
      : {
          listItemGroup: null,
          customBucket: null,
          unit: undefined,
          filterOperator: undefined,
          filterValue: undefined,
        }),
  };

  if (forFilter) {
    // turn into PropertyFilter
    newSegmentation = PropertyFilterFactory.createPropertyFilter(newSegmentation, {
      displayContext: DisplayContext.Default,
    });
  } else {
    // default date typecasting to a day breakdown
    if (newType.type === PropertyType.Datetime) {
      newSegmentation.unit = TimeUnit.Day;
    }
  }

  return populateListPropertyAttrs(newSegmentation as T, newType.isList);
}
